import AppLoader from 'components/AppLoader';
import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _, { noop } from 'lodash'
import { Redirect } from 'react-router-dom';
import { checkAuthenticity, hasRole, updateSelectedThemeInCachedSessionData } from 'utils/authService';
import Page403 from 'views/Pages/Page403';
import { setLocalStorage, getLocalStorage, setIsHighContrastModeState, getIsHighContrastModeState } from 'utils/localStorageUtils';
import * as DarkReader from 'darkreader';
import UserAPI from 'restAPI/UserAPI';
import message from 'components/CustomMessage';
import { isHeadlessBrowser } from 'utils/browserUtils';
import { theme as globalTheme } from 'antd-v5';

const ActiveUserContext = React.createContext({refreshAuth: noop});

export function useActiveUserContext(){
    return useContext(ActiveUserContext);
}

export function ActiveUserContextProvider({children, noRedirect, accessRoles = []}){
    const [activeUser, setActiveUser] = useState({});
    const [isAuthentic, setAuthentic] = useState(null);
    const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'light');
    const [isHighContrastMode, setIsHighContrastMode] = useState(() => (document.documentElement.getAttribute('data-high-contrast-mode') === 'true') || getIsHighContrastModeState(true));
    const isDarkTheme = theme === 'dark';
    const { token } = globalTheme.useToken();

    const updateSelectedTheme = useCallback((theme) => {
        let appliedTheme = theme;
        let isHighContrastMode = false;
        if (theme === 'light_high_contrast') {
            appliedTheme = 'light';
            isHighContrastMode = true;
        }
        setIsHighContrastMode(isHighContrastMode);
        updateSelectedThemeInCachedSessionData(theme);
        setTheme(appliedTheme);
    }, []);

    const checkAuth = useCallback(async (refresh = false) => {
        let isAuthentic = false, userData = null, darkLightTheme = null;
        try {
            userData = await checkAuthenticity(refresh);
            isAuthentic = true;
            if(getLocalStorage('source') === 'zendesk'){
                localStorage.removeItem('source');
                return window.open(`${window.location.origin}/api/jwt/zendesk/sso`, '_self', 'noopener noreferrer');
            }

            darkLightTheme = userData?.sessionData?.tenantDefaults?.default_theme;
        } catch (e) {
            const errorCode = _.result(e, 'response.data.error');
            if (errorCode) message.error(_.result(e, 'response.data.message'));
            const hashLocation = window.location.hash.split('#')[1];
            setLocalStorage('hashLocation', hashLocation)
            console.error(e);
        } finally {
            setActiveUser(Object.freeze({...userData, refreshAuth: () => checkAuth(true)}));
            setAuthentic(isAuthentic);
            updateSelectedTheme(darkLightTheme);
        }
    }, [updateSelectedTheme]);

    useEffect(() => {
        // In a headless browser (Playwright/ECS scheduled report export),
        // always render in light mode regardless of the user's saved preference.
        // navigator.webdriver === true in Playwright unless explicitly suppressed.
        const effectiveTheme = isHeadlessBrowser() ? 'light' : theme;
        document.documentElement.setAttribute('data-theme', effectiveTheme);
        document.documentElement.setAttribute('data-high-contrast-mode', isHighContrastMode);
        setIsHighContrastModeState(isHighContrastMode);
        token?.setIsHighContrastMode?.(isHighContrastMode);

        if (effectiveTheme === 'dark') {
            DarkReader.enable({
                brightness: 100,
                contrast: 90,
                sepia: -15,
            }, {
                ignoreInlineStyle: [
                  '[data-dark-reader-ignore="true"]',
                  '[data-dark-reader-ignore="true"] *',
                ]});
        } else {
            DarkReader.disable();
        }
    }, [theme, isHighContrastMode, token]);

    useEffect(() => {
      checkAuth();
    }, [checkAuth]);

    const toggleTheme = useCallback(
       async () => {
            const newTheme = theme === 'dark' ? 'light' : 'dark';
            updateSelectedTheme(newTheme);
            await UserAPI.updateUserPreference({default_theme: newTheme }).promise;
          },
      [theme, updateSelectedTheme]);

    const value = useMemo(() => {
      return { ...activeUser, theme, setTheme: updateSelectedTheme, isDarkTheme, toggleTheme, isHighContrastMode };
    }, [activeUser, theme, updateSelectedTheme, isDarkTheme, toggleTheme, isHighContrastMode]);

    if(isAuthentic === null){
        return <AppLoader />;
    }
    if (!isAuthentic && !noRedirect) {
        return <Redirect to={`/login?${window.location.pathname}`}/>;
    }
    if(!_.isEmpty(accessRoles) && !hasRole(accessRoles)){
        return <Page403 />;
    }
    return (
        <ActiveUserContext.Provider value={value} >
            {children}
        </ActiveUserContext.Provider>
    )
}

export default ActiveUserContext;

export function withActiveUser(Component){
    return forwardRef(function ActiveUser(props, ref) {
        const activeUser = useActiveUserContext();
        return (
            <Component {...props} activeUser={activeUser} ref={ref} />
        )
    })
}